import React, { useEffect, useMemo } from "react";
import { FieldArray } from "formik";
import {
  type Config,
  TokenHistoryLabel,
  isRepeatableCta
} from "kadow-jetpack-model";
import { type FormikFormChildProps } from "../FormikForm/FormikFormWrapperForJetpackConfig";
import { DEFAULT_LINE_DRIFT } from "../../../../utils/constants";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../../designsystem/ds_commons";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import CTAHeader from "./module/CTAHeader";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import {
  buildListOfAcquisitionCTAsToDisplay,
  buildListOfRegularCTAsToDisplay
} from "../../../../utils/helpers/checkCtas";
import UpsellCTAs from "../../../../components/UpsellCTAs/UpsellCTAs";
import { tt } from "../../../../utils/i18n/i18n";
import MobileDashboardHeader from "../../../Layout/Mobile/MobileDashboardHeader";
import { useSubscriptionPlansSelector } from "../../../../redux/actions/app";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import RegularCTAsEditor from "./RegularCTAsEditor";
import RepeatableCTAsEditor from "./RepeatableCTAsEditor";
import "./styles.scss";

const JetpackCTAEditor: React.FC<FormikFormChildProps> = (props) => {
  const { accountToDisplay } = useAccountToDisplaySelector();
  const jetpackConfig = useJetpackConfigSelector();

  const regularCTAsToDisplay = useMemo<TokenHistoryLabel[]>(() => {
    return buildListOfRegularCTAsToDisplay(accountToDisplay, props.config);
  }, [accountToDisplay, props.config]);

  const acquisitionCTAsToDisplay = useMemo<TokenHistoryLabel[]>(() => {
    return buildListOfAcquisitionCTAsToDisplay(accountToDisplay, jetpackConfig);
  }, [accountToDisplay, jetpackConfig]);

  const canRemoveCTA = useMemo(() => {
    if (!accountToDisplay?.saasFeatures?.ctas) {
      return true;
    }
    return Object.keys(accountToDisplay.saasFeatures.ctas).length > 1;
  }, [accountToDisplay]);

  const allSubscriptionPlans = useSubscriptionPlansSelector();

  const PREMIUM_PLANS_IDS = useMemo(() => {
    return (
      allSubscriptionPlans
        ?.filter((p) => p.saasFeatures?.ip)
        ?.map((p) => p._id) ?? []
    );
  }, [allSubscriptionPlans]);

  const FREE_PLANS_IDS = useMemo(() => {
    const ids =
      allSubscriptionPlans?.filter((p) => !p.price).map((p) => p._id) ?? [];
    //?.filter((p) => p.saasFeatures?.ip || (p.price && p.price > 0))
    return ["", null, undefined, ...ids];
  }, [allSubscriptionPlans]);

  const IS_FREE = useMemo(() => {
    return (
      !!accountToDisplay?.saasPlanId &&
      FREE_PLANS_IDS.includes(accountToDisplay?.saasPlanId)
    );
  }, [accountToDisplay, FREE_PLANS_IDS]);

  const displayUpsellCTAs = useMemo(() => {
    return accountToDisplay?.saasPlanId
      ? PREMIUM_PLANS_IDS.includes(accountToDisplay.saasPlanId)
      : false;
  }, [accountToDisplay]);

  const plan = useMemo(() => {
    if (!accountToDisplay || !allSubscriptionPlans?.length) {
      return null;
    }
    return allSubscriptionPlans.find(
      (p) => p._id === accountToDisplay?.saasPlanId
    );
  }, [accountToDisplay, allSubscriptionPlans]);

  const displayRegularCTAs = useMemo(() => {
    /**
     * Backwards compatibility with previous plan system (starter, basic, etc)
     */
    if (jetpackConfig?.ctas) {
      const regularCTACount =
        jetpackConfig?.ctas?.filter((c) => !isRepeatableCta(c.id))?.length ?? 0;

      if (regularCTACount) {
        return true;
      }
    }

    if (accountToDisplay?.saasFeatures?.lty) {
      if (plan?.price) {
        return true;
      }
      return false;
    }

    return true;
  }, [jetpackConfig, accountToDisplay, plan]);

  const isLegacyPlan = (planId: string | undefined | null) => {
    if (!planId) {
      return true;
    }

    const allPlanIds = allSubscriptionPlans?.map((p) => p._id) ?? [];

    return !allPlanIds.includes(planId);
  };

  const displayTitles = useMemo(() => {
    return !IS_FREE || isLegacyPlan(accountToDisplay?.saasPlanId);
  }, [IS_FREE, jetpackConfig, accountToDisplay]);

  useEffect(() => {
    if (!props.config) {
      return;
    }

    const idx = props.config.ctas?.findIndex(
      (cta) => cta.id === TokenHistoryLabel.Receipt
    );

    if (idx !== -1) {
      const cta = props.config.ctas?.[idx];

      if (cta?.params == null) {
        const newCTAs = [...(props.config.ctas || [])];
        newCTAs[idx] = {
          ...newCTAs[idx],
          params: {
            ocr: {
              lineHeightDrift: DEFAULT_LINE_DRIFT,
              knownProductNameArtefact: [],
              excludeProductName: []
            }
          }
        };
        props.setFieldValue("ctas", newCTAs);
      }
    }
  }, [props.config]);

  return (
    <div className="dashboard-v2">
      <MobileDashboardHeader
        title={tt("cta_editor_heading_2")}
        description={tt("cta_editor_paragraph_2")}
      />
      {displayRegularCTAs && (
        <>
          <div className="d-none d-sm-flex flex-column flex-grow-1 mx-4 pt-5">
            {displayTitles && <CTAHeader />}
          </div>
          <KC_Separator size={KC_Sizes.sm} />
        </>
      )}

      <FieldArray name="ctas">
        {(arrayHelpers) => (
          <div className="d-flex flex-column st-gap px-4 pt-2 w-100">
            <RegularCTAsEditor
              displayRegularCTAs={displayRegularCTAs}
              config={props.config}
              setFieldValue={props.setFieldValue}
              setFieldTouched={props.setFieldTouched}
              errors={props.errors}
              touched={props.touched}
              canRemoveCTA={canRemoveCTA}
              regularCTAsToDisplay={regularCTAsToDisplay}
              arrayHelpers={arrayHelpers}
            />

            <RepeatableCTAsEditor
              acquisitionCTAsToDisplay={acquisitionCTAsToDisplay}
              displayTitles={displayTitles}
              config={props.config}
              setFieldValue={props.setFieldValue}
              setFieldTouched={props.setFieldTouched}
            />

            <KC_Separator />
          </div>
        )}
      </FieldArray>

      {!displayUpsellCTAs && (
        <div className="mt-4 mb-4 px-4 upsell-cta-wrapper">
          <UpsellCTAs
            config={props.config}
            regularCTAsToDisplay={regularCTAsToDisplay}
            acquisitionCTAsToDisplay={acquisitionCTAsToDisplay}
          />
        </div>
      )}

      <KC_Padder />
    </div>
  );
};

export default JetpackCTAEditor;

export function createCtaOrderFromCtaList(config: Config) {
  if (!config.ctas || config.ctas.length === 0) {
    return [];
  }

  const ctas_order: string[] =
    config.ctas?.map((cta) => {
      return cta.id;
    }) || [];

  return ctas_order;
}
